import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  Order,
  QuoteRequest,
  QuoteResponse,
  FreightQuoteRequest,
  FreightQuote,
  ApiResponse,
  PaginatedResponse,
  User,
  DealerSalesRep,
  CreateDealerSalesRepDto,
  UpdateDealerSalesRepDto,
  CommissionSettings,
  UpdateCommissionSettingsDto,
  OrderCommission,
  CommissionSummary,
  CommissionByRep,
  MarkCommissionsPaidDto,
  ActivityLogEntry,
} from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle auth errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('auth_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Generic HTTP methods for flexibility
  async get<T = any>(url: string, config?: { params?: Record<string, string> }): Promise<{ data: T }> {
    return this.client.get(url, config);
  }

  async post<T = any>(url: string, data?: any): Promise<{ data: T }> {
    return this.client.post(url, data);
  }

  async put<T = any>(url: string, data?: any): Promise<{ data: T }> {
    return this.client.put(url, data);
  }

  async patch<T = any>(url: string, data?: any): Promise<{ data: T }> {
    return this.client.patch(url, data);
  }

  async delete<T = any>(url: string): Promise<{ data: T }> {
    return this.client.delete(url);
  }

  // UI theme (per-user, follows across devices). Returns raw JSON (new OR
  // legacy shape) — callers normalize via utils/theme.normalizePref.
  // Dealer flasher: how many of my support tickets have an un-opened reply from Keder.
  async getSupportUnreadCount(): Promise<number> {
    try {
      const { data } = await this.client.get('/support/tickets/unread-count');
      return data?.count || 0;
    } catch {
      return 0;
    }
  }

  async getUiTheme(): Promise<any | null> {
    try {
      const { data } = await this.client.get('/dealers/my/ui-theme');
      return data ?? null;
    } catch {
      return null;
    }
  }

  async updateUiTheme(pref: { preset: string; customBg: string; customAccent: string }): Promise<void> {
    await this.client.patch('/dealers/my/ui-theme', pref);
  }

  // Auth
  async login(email: string, password: string): Promise<{ user: User; token: string }> {
    const { data } = await this.client.post('/auth/login', { email, password });
    return data;
  }

  async getProfile(): Promise<User> {
    const { data } = await this.client.get('/auth/profile');
    return data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.client.post('/auth/change-password', { currentPassword, newPassword });
  }

  async forgotPassword(email: string): Promise<void> {
    await this.client.post('/auth/forgot-password', { email });
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await this.client.post('/auth/reset-password', { token, newPassword });
  }

  // Dashboard Stats
  async getDashboardStats(): Promise<{
    activeOrders: number;
    pendingOrders: number;
    inProductionOrders: number;
    thisMonthTotal: number;
    thisMonthCount: number;
  }> {
    const { data } = await this.client.get('/orders/stats/dashboard');
    return data;
  }

  // Orders
  async getOrders(params?: {
    status?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
    pageSize?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Order> & { data?: Order[] }> {
    const { data } = await this.client.get('/orders', { params });
    return data;
  }

  async getOrder(id: string): Promise<Order> {
    const { data } = await this.client.get(`/orders/${id}`);
    return data;
  }

  async createOrder(orderData: Partial<Order>): Promise<Order> {
    const { data } = await this.client.post('/orders', orderData);
    return data;
  }

  async updateOrder(id: string, orderData: Partial<Order>): Promise<Order> {
    const { data } = await this.client.patch(`/orders/${id}`, orderData);
    return data;
  }

  async submitOrder(id: string, invoiceRequested?: boolean): Promise<Order> {
    const { data } = await this.client.post(`/orders/${id}/submit`, { invoiceRequested });
    return data;
  }

  async cancelOrder(id: string, reason: string): Promise<Order> {
    const { data } = await this.client.post(`/orders/${id}/cancel`, { reason });
    return data;
  }

  async deleteOrder(id: string): Promise<{ success: boolean; message: string }> {
    const { data } = await this.client.delete(`/orders/${id}`);
    return data;
  }

  // Order Items
  async addOrderItem(orderId: string, item: Partial<Order['items']>[0]): Promise<Order> {
    const { data } = await this.client.post(`/orders/${orderId}/items`, item);
    return data;
  }

  async updateOrderItem(
    orderId: string,
    itemId: string,
    item: Partial<Order['items']>[0]
  ): Promise<Order> {
    const { data } = await this.client.patch(`/orders/${orderId}/items/${itemId}`, item);
    return data;
  }

  async deleteOrderItem(orderId: string, itemId: string): Promise<void> {
    await this.client.delete(`/orders/${orderId}/items/${itemId}`);
  }

  // Pricing
  async calculateQuote(request: QuoteRequest): Promise<QuoteResponse> {
    const { data } = await this.client.post('/pricing/quote', request);
    return data;
  }

  async getProductTypes(): Promise<string[]> {
    const { data } = await this.client.get('/pricing/product-types');
    return data;
  }

  async getDimensionLimits(productType: string): Promise<{
    minWidth: number;
    maxWidth: number;
    minHeight: number;
    maxHeight: number;
  }> {
    const { data } = await this.client.get(`/pricing/dimension-limits?productType=${productType}`);
    return data;
  }

  // Freight
  async getFreightQuote(request: FreightQuoteRequest): Promise<FreightQuote> {
    const { data } = await this.client.post('/freight/quote', request);
    return data;
  }

  async calculateFreightDimensions(screens: Array<{ width: number; height: number }>): Promise<{
    length: number;
    width: number;
    height: number;
    weight: number;
    pallets: number;
  }> {
    const { data } = await this.client.get('/pricing/freight/calculate-dimensions', {
      params: {
        widths: screens.map((s) => s.width).join(','),
        heights: screens.map((s) => s.height).join(','),
      },
    });
    return data;
  }

  // Order Items
  async getOrderItems(orderId: string): Promise<any[]> {
    const { data } = await this.client.get(`/orders/${orderId}/items`);
    return data;
  }

  // Documents
  async getOrderPdf(orderId: string): Promise<Blob> {
    const { data } = await this.client.get(`/orders/${orderId}/pdf`, {
      responseType: 'blob',
    });
    return data;
  }

  // Shortage Info
  async getOrderShortageInfo(orderId: string): Promise<{
    onHold: boolean;
    holdSince: string | null;
    items: Array<{ description: string; shortageQuantity: number; estimatedLeadTimeDays: number }>;
  }> {
    const { data } = await this.client.get(`/production/orders/${orderId}/shortage-info`);
    return data;
  }
}

export const api = new ApiService();

// Convenience wrappers for different API domains
export const ordersApi = {
  list: (params?: { status?: string; page?: number; limit?: number }) =>
    api.getOrders(params),
  get: (id: string) => api.getOrder(id),
  create: (data: any) => api.createOrder(data),
  update: (id: string, data: any) => api.updateOrder(id, data),
  submit: (id: string, invoiceRequested?: boolean) => api.submitOrder(id, invoiceRequested),
  cancel: (id: string, reason: string = 'Cancelled by dealer') => api.cancelOrder(id, reason),
  markLost: async (id: string, reason: string) => {
    const { data } = await api['client'].post(`/orders/${id}/mark-lost`, { reason });
    return data;
  },
  delete: (id: string) => api.deleteOrder(id),
  getItems: (id: string) => api.getOrderItems(id),
  updateParts: async (id: string, payload: any) => {
    const { data } = await api['client'].patch(`/orders/${id}/parts`, payload);
    return data as { success: true; orderId: string; orderNumber: string };
  },
  addItem: (orderId: string, item: any) => api.addOrderItem(orderId, item),
  updateItem: (orderId: string, itemId: string, item: any) =>
    api.updateOrderItem(orderId, itemId, item),
  deleteItem: (orderId: string, itemId: string) => api.deleteOrderItem(orderId, itemId),
  getPdf: (id: string) => api.getOrderPdf(id),
  getShortageInfo: (id: string) => api.getOrderShortageInfo(id),
  getProductionProgress: async (id: string) => {
    try {
      const { data } = await api.get(`/orders/${id}/production-progress`);
      return data;
    } catch {
      return null;
    }
  },

  // Install & Warranty actions
  scheduleInstall: async (id: string, data: { installDate: string; installTime?: string; installerName?: string; notes?: string }) => {
    const { data: result } = await api['client'].post(`/orders/${id}/schedule-install`, data);
    return result;
  },
  rescheduleInstall: async (id: string, data: { installDate: string; installTime?: string; installerName?: string; notes?: string }) => {
    const { data: result } = await api['client'].post(`/orders/${id}/reschedule-install`, data);
    return result;
  },
  markInstalled: async (id: string, data: { installerName?: string }) => {
    const { data: result } = await api['client'].post(`/orders/${id}/mark-installed`, data);
    return result;
  },
  registerWarranty: async (id: string, warrantyRegistrationId: string) => {
    const { data: result } = await api['client'].post(`/orders/${id}/register-warranty`, { warrantyRegistrationId });
    return result;
  },

  // Install calendar
  getScheduledInstalls: async (dateFrom?: string, dateTo?: string) => {
    const { data } = await api['client'].get('/orders/installs/scheduled', { params: { dateFrom, dateTo } });
    return data;
  },
  getInstallCalendar: async (month: number, year: number) => {
    const { data } = await api['client'].get('/orders/installs/calendar', { params: { month, year } });
    return data;
  },

  // Invoice
  getInvoice: async (id: string) => {
    const { data } = await api['client'].get(`/orders/${id}/invoice`);
    return data;
  },
  getInvoiceChanges: async (id: string) => {
    const { data } = await api['client'].get(`/orders/${id}/invoice-changes`);
    return data as Array<{ date: string; reason: string; delta: number; fromTotal: number; toTotal: number }>;
  },
  markInvoiceViewed: async (id: string) => {
    const { data } = await api['client'].post(`/orders/${id}/invoice/viewed`);
    return data;
  },
  requestInvoiceCorrection: async (id: string, message: string) => {
    const { data } = await api['client'].post(`/orders/${id}/invoice/request-correction`, { message });
    return data;
  },
};

export const dealerEventsApi = {
  getAll: async (filter?: { month?: number; year?: number; eventType?: string }) => {
    const { data } = await api['client'].get('/dealer-events', { params: filter });
    return data;
  },
  get: async (id: string) => {
    const { data } = await api['client'].get(`/dealer-events/${id}`);
    return data;
  },
  create: async (event: {
    eventType: string;
    title: string;
    eventDate: string;
    eventTime?: string;
    durationMinutes?: number;
    location?: string;
    relatedLeadId?: string;
    relatedOrderId?: string;
    notes?: string;
    reminder?: string;
  }) => {
    const { data } = await api['client'].post('/dealer-events', event);
    return data;
  },
  update: async (id: string, event: any) => {
    const { data } = await api['client'].put(`/dealer-events/${id}`, event);
    return data;
  },
  delete: async (id: string) => {
    const { data } = await api['client'].delete(`/dealer-events/${id}`);
    return data;
  },
  getCalendar: async (month: number, year: number) => {
    const { data } = await api['client'].get('/dealer-events/calendar', { params: { month, year } });
    return data;
  },
};

export const pricingApi = {
  calculateQuote: (request: any) => api.calculateQuote(request),
  getProductTypes: () => api.getProductTypes(),
  getDimensionLimits: (productType: string) => api.getDimensionLimits(productType),
};

export const freightApi = {
  getQuote: (request: any) => api.getFreightQuote(request),
  calculateDimensions: (screens: Array<{ width: number; height: number }>) =>
    api.calculateFreightDimensions(screens),
  // Live freight estimate from Freightview - returns ALL carriers with markup
  estimate: async (request: {
    destination_zip: string;
    destination_company?: string;
    destination_address?: string;
    destination_city?: string;
    destination_state?: string;
    destination_contact?: string;
    destination_phone?: string;
    order_number?: string;
    length: number;
    width: number;
    height: number;
    weight: number;
    pallets: number;
    boxes?: Array<{ length: number; width: number; height: number; weight: number }>;
    freight_class?: number;
    accessorials?: {
      liftgate?: boolean;
      residential?: boolean;
      inside?: boolean;
    };
  }): Promise<{
    success: boolean;
    rate: number;
    carrier: string;
    service: string;
    transitTime: string;
    fallback?: boolean;
    error?: string;
    freightviewShipmentId?: string;
    allQuotes?: Array<{
      quoteId: string;
      carrierName: string;
      carrierCode: string;
      serviceDescription: string;
      totalAmount: number;
      transitTime: string;
      transitDaysMin: number;
      transitDaysMax: number;
    }>;
    // markupPercent is stripped for dealers — see shipping-cost.util.
  }> => {
    const { data } = await api['client'].post('/freight/estimate', request);
    return data;
  },
};

// Parcel API for UPS/FedEx/USPS small package shipping
export const parcelApi = {
  classifyCart: async (items: Array<{
    category: string;
    quantity: number;
    lengthInches?: number;
    weight?: number;
  }>): Promise<{
    type: 'small_package' | 'ltl_freight' | 'mixed';
    reason: string;
    suggestedPackages: Array<{ weight: number; length: number; width: number; height: number }>;
  }> => {
    const { data } = await api['client'].post('/parcel/classify-cart', { items });
    return data;
  },
  getRates: async (request: {
    destinationZip: string;
    destinationCity?: string;
    destinationState?: string;
    destinationAddress?: string;
    residential?: boolean;
    packages: Array<{ weight: number; length: number; width: number; height: number }>;
    /** Lets the API withhold carriers it cannot produce a label for. */
    skus?: string[];
  }): Promise<{
    success: boolean;
    // The API deliberately does NOT return baseAmount / markupAmount /
    // markupPercent to dealers. Carrier cost stays server-side — shipping the
    // markup percent alongside the total is enough to reverse our rate.
    // Do not add them back here.
    quotes?: Array<{
      carrier: string;
      serviceCode: string;
      serviceName: string;
      totalAmount: number;
      transitDays: number;
      transitDescription: string;
      guaranteedDelivery?: boolean;
    }>;
    poBoxWarning?: boolean;
    error?: string;
  }> => {
    const { data } = await api['client'].post('/parcel/rates', request);
    return data;
  },
};

// Payment API for Stripe integration
export const paymentApi = {
  createCheckout: async (request: {
    invoice: any;
    order: any;
    dealer: {
      id?: string;
      dealerId?: string;
      email?: string;
      company?: string;
      contactName?: string;
    };
    paymentAmount: number; // in cents
    authorizedBy?: string;
    paymentMethodType?: 'card' | 'us_bank_account';
    existingOrderId?: string; // If provided, update existing order instead of creating new
    creditApplied?: number; // store-credit dollars to apply against this payment
  }): Promise<{
    success: boolean;
    checkoutUrl?: string;
    sessionId?: string;
    orderId?: string;
    error?: string;
  }> => {
    const { data } = await api['client'].post('/payment/create-checkout', request);
    return data;
  },

  getAchSettings: async (): Promise<{ achDiscountPercent: number; authorizeAchDiscountPercent?: number; helcimAchDiscountPercent?: number }> => {
    const { data } = await api['client'].get('/payment/ach-settings');
    return data;
  },

  createMultiInvoiceCheckout: async (request: {
    payments: Array<{ orderId: string; amount: number }>;
    paymentMethodType?: 'card' | 'us_bank_account';
  }): Promise<{
    success: boolean;
    checkoutUrl?: string;
    sessionId?: string;
    error?: string;
  }> => {
    const { data } = await api['client'].post('/payment/create-multi-invoice-checkout', request);
    return data;
  },

  getStripePublicKey: async (): Promise<{ publishableKey: string }> => {
    const { data } = await api['client'].get('/payment/stripe-public-key');
    return data;
  },

  getStripeSavedMethods: async (): Promise<Array<{
    id: string;
    type: 'card' | 'us_bank_account';
    brand?: string;
    last4?: string;
    expMonth?: number;
    expYear?: number;
    accountType?: string;
  }>> => {
    const { data } = await api['client'].get('/payment/saved-methods');
    return data || [];
  },

  createMultiInvoiceIntent: async (request: {
    payments: Array<{ orderId: string; amount: number }>;
    paymentMethodId?: string;
    cardFunding?: 'credit' | 'debit' | 'prepaid' | 'unknown' | null;
    creditApplied?: number;
    paymentElement?: boolean;
  }): Promise<{
    success: boolean;
    clientSecret?: string;
    paymentIntentId?: string;
    baseAmount?: number;
    creditApplied?: number;
    surchargeAmount?: number;
    surchargePercent?: number;
    totalAmount?: number;
    surchargeSkipped?: boolean;
    surchargeSkipReason?: string;
    error?: string;
  }> => {
    const { data } = await api['client'].post('/payment/create-multi-invoice-intent', request);
    return data;
  },

  createPendingPartsOrder: async (request: {
    items: any[];
    subtotal: number;
    shipping: number;
    total: number;
    shippingMethod: string;
    shippingAddress?: { street?: string; city?: string; state?: string; zip?: string } | null;
    freightInfo?: any;
    dealer: { id?: string; dealerId?: string; email: string; company?: string; contactName?: string };
    [key: string]: any;
  }): Promise<{ orderId: string; orderNumber: string }> => {
    const { data } = await api['client'].post('/payment/create-pending-parts-order', request);
    return data;
  },

  submitPartsForInvoice: async (request: {
    items: any[];
    subtotal: number;
    shipping: number;
    total: number;
    shippingMethod: string;
    shippingAddress?: { street?: string; city?: string; state?: string; zip?: string } | null;
    freightInfo?: any;
    dealer: { id?: string; dealerId?: string; email: string; company?: string; contactName?: string };
    [key: string]: any;
  }): Promise<{ orderId: string; orderNumber: string }> => {
    const { data } = await api['client'].post('/payment/submit-parts-for-invoice', request);
    return data;
  },
};

// Authorize.net API
export const authorizeApi = {
  charge: async (request: {
    opaqueDataDescriptor: string;
    opaqueDataValue: string;
    amount: number; // cents
    orderId: string;
    orderNumber?: string;
    dealer: {
      email: string;
      company?: string;
      contactName?: string;
    };
    saveForFuture?: boolean;
  }): Promise<{
    success: boolean;
    transactionId?: string;
    savedProfileId?: string;
    error?: string;
  }> => {
    const { data } = await api['client'].post('/authorize/charge', request);
    return data;
  },

  getSavedMethods: async (): Promise<Array<{
    id: string;
    paymentType: 'card' | 'bank_account';
    cardBrand?: string;
    cardLast4?: string;
    cardExpMonth?: string;
    cardExpYear?: string;
    bankName?: string;
    bankAccountType?: string;
    bankLast4?: string;
    isDefault: boolean;
    label?: string;
    createdAt: string;
  }>> => {
    const { data } = await api['client'].get('/authorize/saved-methods');
    return data;
  },

  chargeSaved: async (request: {
    paymentProfileId: string;
    amount: number; // cents
    orderId: string;
    orderNumber?: string;
  }): Promise<{
    success: boolean;
    transactionId?: string;
    error?: string;
  }> => {
    const { data } = await api['client'].post('/authorize/charge-saved', request);
    return data;
  },

  chargeBankAccount: async (request: {
    opaqueDataDescriptor: string;
    opaqueDataValue: string;
    amount: number; // cents
    orderId: string;
    orderNumber?: string;
    dealer: {
      email: string;
      company?: string;
      contactName?: string;
    };
    saveForFuture?: boolean;
    bankName?: string;
    accountType?: string;
    accountLast4?: string;
  }): Promise<{
    success: boolean;
    transactionId?: string;
    savedProfileId?: string;
    error?: string;
  }> => {
    const { data } = await api['client'].post('/authorize/charge-bank', request);
    return data;
  },

  saveBankAccount: async (request: {
    opaqueDataDescriptor: string;
    opaqueDataValue: string;
    bankName?: string;
    accountType?: string;
    accountLast4?: string;
  }): Promise<{
    success: boolean;
    profileId?: string;
    error?: string;
  }> => {
    const { data } = await api['client'].post('/authorize/save-bank-account', request);
    return data;
  },

  deleteSavedMethod: async (id: string): Promise<{ success: boolean; error?: string }> => {
    const { data } = await api['client'].delete(`/authorize/saved-methods/${id}`);
    return data;
  },
};

// ─── Helcim ACH API ─────────────────────────────────────────────────

export const helcimApi = {
  initializeCheckout: async (request: {
    amount: number; // cents
    orderId: string;
    orderNumber?: string;
    dealer: {
      email: string;
      company?: string;
      contactName?: string;
    };
  }): Promise<{
    checkoutToken: string;
    secretToken: string;
    customerCode: string;
    achDiscountPercent: number;
    achDiscountAmountCents: number;
    chargedAmountCents: number;
  }> => {
    const { data } = await api['client'].post('/helcim/initialize-checkout', request);
    return data;
  },

  validatePayment: async (request: {
    checkoutToken: string;
    secretToken: string;
    transactionData: any;
    orderId: string;
    amountCents: number;
    achDiscountPercent?: number;
    achDiscountAmountCents?: number;
  }): Promise<{
    success: boolean;
    transactionId?: string;
    error?: string;
  }> => {
    const { data } = await api['client'].post('/helcim/validate-payment', request);
    return data;
  },

  chargeSavedBank: async (request: {
    profileId: string;
    amount: number; // cents
    orderId: string;
    orderNumber?: string;
  }): Promise<{
    success: boolean;
    transactionId?: string;
    achDiscountPercent?: number;
    achDiscountAmountCents?: number;
    chargedAmountCents?: number;
    error?: string;
  }> => {
    const { data } = await api['client'].post('/helcim/charge-saved-bank', request);
    return data;
  },

  getSavedBankAccounts: async (): Promise<Array<{
    id: string;
    paymentType: 'bank_account';
    bankName?: string;
    bankAccountType?: string;
    bankLast4?: string;
    isDefault: boolean;
    label?: string;
    createdAt: string;
    provider: 'helcim';
  }>> => {
    const { data } = await api['client'].get('/helcim/saved-bank-accounts');
    return data;
  },

  deleteSavedBankAccount: async (id: string): Promise<{ success: boolean; error?: string }> => {
    const { data } = await api['client'].delete(`/helcim/saved-bank-accounts/${id}`);
    return data;
  },
};

export const authApi = {
  login: (email: string, password: string) => api.login(email, password),
  getProfile: () => api.getProfile(),
  changePassword: (current: string, newPass: string) => api.changePassword(current, newPass),
  forgotPassword: (email: string) => api.forgotPassword(email),
  resetPassword: (token: string, newPassword: string) => api.resetPassword(token, newPassword),
  // One-click magic link verify — used by the Pay Online CTA in invoice emails.
  // Returns the JWT + an optional redirectTo path (e.g. /orders/<id>).
  verifyMagicLink: async (token: string): Promise<{ token: string; accessToken: string; user: any; redirectTo?: string }> => {
    const { data } = await api['client'].post('/auth/verify', { token });
    return data;
  },
  updateAddress: async (address: {
    street?: string;
    city: string;
    state: string;
    zip: string;
  }): Promise<{ success: boolean; message: string }> => {
    const { data } = await api['client'].post('/auth/update-address', address);
    return data;
  },
  checkTermsStatus: async (): Promise<{ accepted: boolean }> => {
    const { data } = await api['client'].get('/auth/terms-status');
    return data;
  },
  acceptTerms: async (): Promise<{ success: boolean }> => {
    const { data } = await api['client'].post('/auth/accept-terms');
    return data;
  },
  // Dealer Logo
  uploadLogo: async (file: File): Promise<{ success: boolean; logoUrl: string; publicId: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api['client'].post('/auth/upload-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  deleteLogo: async (): Promise<{ success: boolean }> => {
    const { data } = await api['client'].delete('/auth/delete-logo');
    return data;
  },
  getLogo: async (): Promise<{ logoUrl: string | null }> => {
    const { data } = await api['client'].get('/auth/logo');
    return data;
  },
};

// Quotes API
export interface SavedQuote {
  id: string;
  dealerId: string;
  name: string;
  configuration: {
    productType: string;
    screens: Array<{
      width: number;
      height: number;
      uChannel: boolean;
    }>;
    extraRemote: boolean;
    dealerDiscount: number;
  };
  pricing: {
    screensMSRP: number;
    screensDealer: number;
    remoteMSRP: number;
    remoteDealer: number;
    uChannelMSRP: number;
    uChannelDealer: number;
    uChannelFeet: number;
    packaging: number;
    grandTotalMSRP: number;
    grandTotalDealer: number;
  };
  createdAt: string;
  updatedAt: string;
  validUntil?: string;   // 30-day validity (created + 30d)
  isExpired?: boolean;   // true once past the window
}

// Custom Products (Dealer catalog for price builder)
export interface CustomProduct {
  id: string;
  dealerId: string;
  name: string;
  category: string;
  description: string;
  unitCost: number;
  msrpPrice: number;
  sku: string;
  imageUrl: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export const customProductsApi = {
  list: async (category?: string): Promise<CustomProduct[]> => {
    const { data } = await api['client'].get('/dealer-custom-products', { params: category ? { category } : {} });
    return data;
  },
  create: async (product: { name: string; category?: string; description?: string; unitCost?: number; msrpPrice: number; sku?: string }): Promise<CustomProduct> => {
    const { data } = await api['client'].post('/dealer-custom-products', product);
    return data;
  },
  update: async (id: string, product: Partial<{ name: string; category: string; description: string; unitCost: number; msrpPrice: number; sku: string; isActive: boolean }>): Promise<CustomProduct> => {
    const { data } = await api['client'].patch(`/dealer-custom-products/${id}`, product);
    return data;
  },
  delete: async (id: string): Promise<void> => {
    await api['client'].delete(`/dealer-custom-products/${id}`);
  },
  categories: async (): Promise<string[]> => {
    const { data } = await api['client'].get('/dealer-custom-products/categories');
    return data;
  },
};

export const quotesApi = {
  list: async (page = 1, pageSize = 20): Promise<{ data: SavedQuote[]; total: number }> => {
    const { data } = await api['client'].get('/quotes', { params: { page, pageSize } });
    return data;
  },
  get: async (id: string): Promise<SavedQuote> => {
    const { data } = await api['client'].get(`/quotes/${id}`);
    return data;
  },
  create: async (quote: { name: string; configuration: SavedQuote['configuration']; pricing: SavedQuote['pricing'] }): Promise<SavedQuote> => {
    const { data } = await api['client'].post('/quotes', quote);
    return data;
  },
  update: async (id: string, quote: Partial<{ name: string; configuration: SavedQuote['configuration']; pricing: SavedQuote['pricing'] }>): Promise<SavedQuote> => {
    const { data } = await api['client'].put(`/quotes/${id}`, quote);
    return data;
  },
  delete: async (id: string): Promise<void> => {
    await api['client'].delete(`/quotes/${id}`);
  },
  sendEstimate: async (data: {
    customerEmail: string;
    customerName: string;
    screens: Array<{ index: number; width: number; height: number; productType?: string; msrpPrice: number }>;
    totalMsrp: number;
    notes?: string;
    extras?: Array<{ label: string; amount: number }>;
    subtotal?: number;
    previewImages?: string[];
  }): Promise<{ success: boolean; message: string }> => {
    const { data: result } = await api['client'].post('/quotes/send-estimate', data);
    return result;
  },
};

// Warranty API
export const warrantyApi = {
  // Registrations
  listRegistrations: async (params?: {
    search?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ data: any[]; total: number; totalPages: number }> => {
    const { data } = await api['client'].get('/warranty/registrations', { params });
    return data;
  },
  getRegistration: async (id: string): Promise<any> => {
    const { data } = await api['client'].get(`/warranty/registrations/${id}`);
    return data;
  },
  createRegistration: async (registration: {
    orderId?: string;
    orderNumber?: string;
    customerName: string;
    customerEmail: string;
    customerPhone?: string;
    customerAddress?: string;
    customerCity?: string;
    customerState?: string;
    customerZip?: string;
    installDate: string;
    installerName?: string;
    installerCompany?: string;
    photoUrls: string[];
    notes?: string;
  }): Promise<any> => {
    const { data } = await api['client'].post('/warranty/registrations', registration);
    return data;
  },

  // RMAs
  listRmas: async (params?: {
    search?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ data: any[]; total: number; totalPages: number }> => {
    const { data } = await api['client'].get('/warranty/rma', { params });
    return data;
  },
  getRma: async (id: string): Promise<any> => {
    const { data } = await api['client'].get(`/warranty/rma/${id}`);
    return data;
  },
  createRma: async (rma: {
    warrantyRegistrationId?: string;
    orderId?: string;
    orderNumber?: string;
    component: string;
    issueType: string;
    description: string;
    photoUrls: string[];
  }): Promise<any> => {
    const { data } = await api['client'].post('/warranty/rma', rma);
    return data;
  },

  // Order lookup
  lookupOrder: async (orderNumber: string): Promise<any> => {
    const { data } = await api['client'].get(`/warranty/lookup/order/${orderNumber}`);
    return data;
  },

  // Get orders ready for warranty registration (status='installed')
  getOrdersReadyForRegistration: async (): Promise<any[]> => {
    const { data} = await api['client'].get('/warranty/orders-ready-for-registration');
    return data;
  },
};

// Calendar Sync API
export const calendarSyncApi = {
  getToken: async (): Promise<{ token: string; subscriptionUrl: string }> => {
    const { data } = await api['client'].get('/calendar-sync/token');
    return data;
  },
  regenerateToken: async (): Promise<{ token: string; subscriptionUrl: string; message: string }> => {
    const { data } = await api['client'].post('/calendar-sync/regenerate');
    return data;
  },
  getGoogleCalendarUrl: async (eventType: 'install' | 'follow_up' | 'custom', eventId: string): Promise<{ url: string }> => {
    const { data } = await api['client'].get(`/calendar-events/${eventType}/${eventId}/google`);
    return data;
  },
  downloadEventICS: async (eventType: 'install' | 'follow_up' | 'custom', eventId: string): Promise<void> => {
    const response = await api['client'].get(`/calendar-events/${eventType}/${eventId}/ics`, {
      responseType: 'blob',
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'event.ics');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Dealer Leads API
export const dealerLeadsApi = {
  list: async (params?: {
    status?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ data: any[]; total: number }> => {
    const { data } = await api['client'].get('/dealer-leads', { params });
    return data;
  },
  get: async (id: string): Promise<any> => {
    const { data } = await api['client'].get(`/dealer-leads/${id}`);
    return data;
  },
  create: async (lead: any): Promise<any> => {
    const { data } = await api['client'].post('/dealer-leads', lead);
    return data;
  },
  update: async (id: string, lead: any): Promise<any> => {
    const { data } = await api['client'].put(`/dealer-leads/${id}`, lead);
    return data;
  },
  delete: async (id: string): Promise<void> => {
    await api['client'].delete(`/dealer-leads/${id}`);
  },
  addActivity: async (leadId: string, activity: any): Promise<any> => {
    const { data } = await api['client'].post(`/dealer-leads/${leadId}/activities`, activity);
    return data;
  },
  getActivities: async (leadId: string): Promise<any[]> => {
    const { data } = await api['client'].get(`/dealer-leads/${leadId}/activities`);
    return data;
  },
};

// Dealer Sales Reps API (Admin only)
export const dealerSalesRepsApi = {
  list: async (params?: {
    role?: 'sales_rep' | 'sales_manager';
    status?: 'active' | 'inactive' | 'terminated';
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ salesReps: DealerSalesRep[]; pagination: { page: number; limit: number; total: number; pages: number } }> => {
    const { data } = await api['client'].get('/dealer-sales-reps', { params });
    return data;
  },

  get: async (id: string): Promise<DealerSalesRep> => {
    const { data } = await api['client'].get(`/dealer-sales-reps/${id}`);
    return data;
  },

  getMe: async (): Promise<DealerSalesRep> => {
    const { data } = await api['client'].get('/dealer-sales-reps/me');
    return data;
  },

  create: async (salesRep: CreateDealerSalesRepDto): Promise<DealerSalesRep> => {
    const { data } = await api['client'].post('/dealer-sales-reps', salesRep);
    return data;
  },

  update: async (id: string, updates: UpdateDealerSalesRepDto): Promise<DealerSalesRep> => {
    const { data } = await api['client'].patch(`/dealer-sales-reps/${id}`, updates);
    return data;
  },

  deactivate: async (id: string): Promise<{ deactivated: boolean }> => {
    const { data } = await api['client'].delete(`/dealer-sales-reps/${id}`);
    return data;
  },

  resetPassword: async (id: string, newPassword: string): Promise<{ message: string }> => {
    const { data } = await api['client'].post(`/dealer-sales-reps/${id}/reset-password`, { newPassword });
    return data;
  },

  getStats: async (id: string): Promise<{
    ordersThisMonth: number;
    commissionsThisMonth: number;
    totalOrders: number;
    totalCommissions: number;
  }> => {
    const { data } = await api['client'].get(`/dealer-sales-reps/${id}/stats`);
    return data;
  },
};

// Dealer Commission Settings API (Admin only)
export const dealerCommissionSettingsApi = {
  get: async (): Promise<CommissionSettings> => {
    const { data } = await api['client'].get('/dealer-commission-settings');
    return data;
  },

  update: async (updates: UpdateCommissionSettingsDto): Promise<CommissionSettings> => {
    const { data } = await api['client'].patch('/dealer-commission-settings', updates);
    return data;
  },
};

// Dealer Order Commissions API
export const dealerOrderCommissionsApi = {
  list: async (params?: {
    salesRepId?: string;
    status?: 'pending' | 'earned' | 'paid' | 'voided';
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<{ commissions: OrderCommission[]; pagination: { page: number; limit: number; total: number; pages: number } }> => {
    const { data } = await api['client'].get('/dealer-order-commissions', { params });
    return data;
  },

  getSummary: async (params?: {
    salesRepId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<CommissionSummary> => {
    const { data } = await api['client'].get('/dealer-order-commissions/summary', { params });
    return data;
  },

  getByRep: async (params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<CommissionByRep[]> => {
    const { data } = await api['client'].get('/dealer-order-commissions/by-rep', { params });
    return data;
  },

  getMyCommissions: async (params?: {
    status?: 'pending' | 'earned' | 'paid';
    page?: number;
    limit?: number;
  }): Promise<{ commissions: OrderCommission[]; pagination: { page: number; limit: number; total: number; pages: number } }> => {
    const { data } = await api['client'].get('/dealer-order-commissions/my-commissions', { params });
    return data;
  },

  markAsPaid: async (payload: MarkCommissionsPaidDto): Promise<{ updated: number; message: string }> => {
    const { data } = await api['client'].post('/dealer-order-commissions/mark-paid', payload);
    return data;
  },

  exportCsv: async (params?: {
    salesRepId?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<void> => {
    const response = await api['client'].get('/dealer-order-commissions/export-csv', {
      params,
      responseType: 'blob',
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const filename = `commissions-${new Date().toISOString().split('T')[0]}.csv`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Dealer Activity Log API (Admin/Manager only)
export const dealerActivityLogApi = {
  list: async (params?: {
    userId?: string;
    action?: string;
    entityType?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<{ activities: ActivityLogEntry[]; pagination: { page: number; limit: number; total: number; pages: number } }> => {
    const { data } = await api['client'].get('/dealer-activity-log', { params });
    return data;
  },
};

// Uploads API
export const uploadsApi = {
  uploadWarrantyPhoto: async (file: File): Promise<{ url: string; publicId: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api['client'].post('/uploads/warranty-photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  uploadRmaPhoto: async (file: File): Promise<{ url: string; publicId: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api['client'].post('/uploads/rma-photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  uploadImage: async (file: File): Promise<{ url: string; publicId: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api['client'].post('/uploads/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  uploadVisualizerScreenshot: async (base64Image: string, orderId?: string): Promise<{ url: string; publicId: string }> => {
    const response = await api['client'].post('/uploads/visualizer-screenshot', {
      image: base64Image,
      orderId,
    });

    return response.data;
  },
};

// Dealer Credit Account API
export const dealerCreditApi = {
  getMyAccount: async (): Promise<{
    netAccountEnabled: boolean;
    creditLimit: number;
    creditBalance: number;
    creditAvailable: number;
    netTermsDays: number;
    accountStatus: string;
    hasOverdue: boolean;
    oldestOverdueDays: number | null;
  }> => {
    const { data } = await api['client'].get('/dealer-credit/my-account');
    return data;
  },
  getMyTransactions: async (params?: {
    type?: string;
    page?: number;
    limit?: number;
  }): Promise<{ transactions: any[]; pagination: { page: number; limit: number; total: number; pages: number } }> => {
    const { data } = await api['client'].get('/dealer-credit/my-transactions', { params });
    return data;
  },
  getMyAvailable: async (): Promise<{ available: number }> => {
    const { data } = await api['client'].get('/dealer-credit/my-available');
    return data;
  },
};

// Company Events API (for company-wide events, holidays, dealer meetings, etc.)
export const companyEventsApi = {
  getUpcoming: async (limit: number = 5): Promise<any[]> => {
    const { data } = await api['client'].get('/company-events/upcoming', { params: { limit } });
    return data;
  },
  getDealerEvents: async (startDate?: string, endDate?: string): Promise<any[]> => {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const { data } = await api['client'].get('/company-events/dealer', { params });
    return data;
  },
  getHolidays: async (year?: number): Promise<any[]> => {
    const { data } = await api['client'].get('/company-events/holidays', { params: year ? { year } : {} });
    return data;
  },
};

// Dealer-self-service account state — used by the global block gate
export const dealerAccountApi = {
  getMyAccountStatus: async (): Promise<{
    newOrdersBlocked: boolean;
    newOrdersBlockReason: string | null;
    autoConsolidateOrders?: boolean;
    flatRateThresholdScreens?: number | null;
    flatRateShippingPerScreen?: number | null;
    willCallAllowed?: boolean;
    dealerState?: string | null;
  }> => {
    const { data } = await api['client'].get('/dealers/my/account-status');
    return data;
  },
};

// Consolidated shipments — read-only for dealers
export const shipmentsApi = {
  listMy: async (includeCompleted = false): Promise<any[]> => {
    const { data } = await api['client'].get('/shipments/my', { params: { includeCompleted } });
    return data;
  },
  getMy: async (id: string): Promise<any> => {
    const { data } = await api['client'].get(`/shipments/my/${id}`);
    return data;
  },
  // Dealer self-service: list own orders eligible to bundle with a new one
  getEligibleToBundle: async (): Promise<Array<{
    id: string;
    order_number: string;
    sidemark: string | null;
    status: string;
    po_number: string | null;
    created_at: string;
    item_count: number;
    deposit_paid: boolean;
    paid_in_full: boolean;
    shipping_method: string | null;
    order_type: string;
  }>> => {
    const { data } = await api['client'].get('/shipments/my/eligible/to-bundle');
    return data || [];
  },
  // Dealer self-service: remove own order from shipment (only while building/quoted)
  unbundleOrder: async (shipmentId: string, orderId: string): Promise<any> => {
    const { data } = await api['client'].post(`/shipments/my/${shipmentId}/remove-order/${orderId}`);
    return data;
  },
};

// Dealer Email Templates API
export const emailTemplatesApi = {
  list: async (): Promise<any[]> => {
    const { data } = await api['client'].get('/dealer-email-templates');
    return data;
  },
  get: async (id: string): Promise<any> => {
    const { data } = await api['client'].get(`/dealer-email-templates/${id}`);
    return data;
  },
  create: async (template: { name: string; subject: string; templateType?: string; description?: string; contentSections?: Record<string, any> }): Promise<any> => {
    const { data } = await api['client'].post('/dealer-email-templates', template);
    return data;
  },
  clone: async (id: string): Promise<any> => {
    const { data } = await api['client'].post(`/dealer-email-templates/clone/${id}`);
    return data;
  },
  update: async (id: string, updates: Record<string, any>): Promise<any> => {
    const { data } = await api['client'].patch(`/dealer-email-templates/${id}`, updates);
    return data;
  },
  delete: async (id: string): Promise<void> => {
    await api['client'].delete(`/dealer-email-templates/${id}`);
  },
  preview: async (id: string, mergeData?: Record<string, string>): Promise<{ html: string; subject: string }> => {
    const { data } = await api['client'].post(`/dealer-email-templates/${id}/preview`, { mergeData });
    return data;
  },
  exportHtml: async (id: string): Promise<{ html: string; subject: string; mergeFields: string[] }> => {
    const { data } = await api['client'].post(`/dealer-email-templates/${id}/export`);
    return data;
  },
  send: async (id: string, payload: { customerEmail: string; customerName: string; subject?: string; mergeData?: Record<string, string>; cc?: string }): Promise<{ success: boolean; message: string }> => {
    const { data } = await api['client'].post(`/dealer-email-templates/${id}/send`, payload);
    return data;
  },
  sendTest: async (id: string, testEmail: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await api['client'].post(`/dealer-email-templates/${id}/send-test`, { testEmail });
    return data;
  },
  getOrderMergeData: async (templateId: string, orderId: string): Promise<Record<string, string>> => {
    const { data } = await api['client'].get(`/dealer-email-templates/${templateId}/order-data/${orderId}`);
    return data;
  },
};

// Fabric Settings API (pricing + roll widths for fabric ordering)
export const fabricSettingsApi = {
  getCatalog: async (): Promise<{
    fabricTypes: {
      id: string;
      code: string;
      name: string;
      pricingCategory: string;
      group: string;
      sortOrder: number;
      colors: { id: string; code: string; name: string; partNumber: string | null; rgbaValue: string | null; sortOrder: number }[];
    }[];
  }> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/fabric-settings/catalog`);
    if (!response.ok) throw new Error('Failed to fetch fabric catalog');
    return response.json();
  },
  getRollWidths: async (): Promise<{ fabricType: string; fabricCode: string; rollWidth: number; maxPanelCut: number }[]> => {
    const { data } = await api['client'].get('/fabric-settings/roll-widths');
    return data;
  },
  getFabricOrdering: async (): Promise<{
    fabrics: {
      fabricType: string;
      category: string;
      bomPerLinearInch: number;
      rollWidth: number;
      colors: string[];
    }[];
    assemblyParts: {
      kederSpline6mm: { costPerInch: number; cutFormula: string };
      kederSpline4mm: { costPerInch: number; cutFormula: string };
      cornerPatches: { costPerScreen: number };
    };
  }> => {
    const { data } = await api['client'].get('/fabric-settings/fabric-ordering');
    return data;
  },
};

/**
 * Parts and replacement ordering.
 *
 * The dealer is identified from the auth token server-side — never sent from
 * here — so these calls cannot act against another dealer's account.
 */
export const partsOrdersApi = {
  /** Sellable assemblies (track, weight bar) for the catalogue. */
  getAssemblies: async (): Promise<Array<{
    code: string; label: string; lengthLabel: string; hasSide: boolean;
  }>> => {
    const { data } = await api['client'].get('/parts-orders/assemblies');
    return data;
  },

  /**
   * A past order's screens with their parts and the cut lengths already on
   * record, so a replacement never needs re-measuring.
   */
  getSourceOrder: async (orderNumber: string): Promise<{
    orderId: string; orderNumber: string; companyName: string; sidemark?: string;
    createdAt: string; lengthsMayDiffer: boolean; lengthsWarning: string | null;
    screens: Array<{
      itemNumber: number; width: number | null; height: number | null;
      fabricType?: string; fabricColor?: string; systemColor?: string;
      assemblies: Array<{
        code: string; label: string; lengthInches: number | null; needsSide: boolean;
        // Present on assemblies copied from the screen itself (the fabric panel).
        orderItemId?: string; note?: string; components?: string[];
      }>;
      parts: Array<{ partNumber: string; description: string; componentType: string; cutLength: number | null; quantity: number }>;
    }>;
  }> => {
    const { data } = await api['client'].get(`/parts-orders/source-order/${encodeURIComponent(orderNumber)}`);
    return data;
  },

  /** Quote without creating. Agrees with create() exactly. */
  price: async (body: {
    lines?: Array<{ partNumber: string; description?: string; color?: string; lengthInches?: number; quantity: number }>;
    assemblies?: Array<{
      assembly: string; lengthInches?: number | null; side?: string; color?: string; quantity: number;
      /** Required for the fabric panel — the screen it is copied from. */
      orderItemId?: string;
    }>;
  }): Promise<{
    lines: Array<{ partNumber: string; description: string; lengthInches?: number; quantity: number; unitCost: number; msrpPrice: number; dealerPrice: number }>;
    subtotal: number; totalCost: number; multiplier: number;
  }> => {
    const { data } = await api['client'].post('/parts-orders/price', body);
    return data;
  },
};

export const inventoryApi = {
  getAccessories: async () => {
    const { data } = await api.get<{ items: any[] }>('/inventory', {
      params: { inventoryGroup: 'Accessories', activeOnly: 'true', limit: '200' },
    });
    return data.items || [];
  },
  getDemoUnits: async () => {
    const { data } = await api.get<{ items: any[] }>('/inventory', {
      params: { inventoryGroup: 'Demo Units', activeOnly: 'true', limit: '200' },
    });
    return data.items || [];
  },
  getSalesKit: async () => {
    const { data } = await api.get<{ items: any[] }>('/inventory', {
      params: { inventoryGroup: 'Sales Kit', activeOnly: 'true', limit: '200' },
    });
    return data.items || [];
  },
};

export default api;
